import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  addToReturnedOrderCart,
  decreaseReturnedOrderQuantity,
} from "../../redux/slices/returnedOrdersCart";
import {
  ArabicCode,
  BASE_LOCAL_IMAGES_DIR,
  CardCounterSpeed,
} from "../../utils/constants/CommonConstants";
import {
  cart_trash_IMG,
  minus_button_IMG,
  white_plus_IMG,
} from "../../utils/constants/Images";

interface ReturnedOrderTableQuantityItemProps {
  TableRow: any;
}

const ReturnedOrderTableQuantityItem: React.FC<
  ReturnedOrderTableQuantityItemProps
> = ({ TableRow }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const uniqueKey = TableRow.productId
    ? `product-${TableRow.productId}`
    : TableRow.offerId
      ? `offer-${TableRow.offerId}`
      : `package-${TableRow.packageId}`;

  // States
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>(
    {},
  );

  const [isIncreasing, setIsIncreasing] = useState<Record<string, boolean>>({});
  const [isDecreasing, setIsDecreasing] = useState<Record<string, boolean>>({});

  const handleAddItemToCart = () => {
    if ((itemQuantities[uniqueKey] || 0) >= TableRow?.ApprovedQuantity) return;

    if (TableRow.ProductId) {
      // Product
      dispatch(
        addToReturnedOrderCart({
          productId: TableRow.ProductId,
          offerId: 0,
          packageId: 0,
          quantity: 1,
          price: TableRow.Price,
          image: TableRow.Image,
          name: TableRow.Name,
          nameAr: TableRow.NameAr,
          nameEn: TableRow.NameEn,
          warehouse: TableRow.Warehouse,
          itemType: "product",
          currency: TableRow.Currency,
          maxReturnedOrderQuantity: TableRow.ApprovedQuantity,
        }),
      );
    } else if (TableRow.PackageId) {
      // Package
      dispatch(
        addToReturnedOrderCart({
          productId: 0,
          offerId: 0,
          packageId: TableRow.PackageId,
          quantity: 1,
          price: TableRow.Price,
          image: TableRow.Image,
          name: TableRow.Package?.Name,
          nameAr: TableRow.Package?.NameAr,
          nameEn: TableRow.Package?.NameEn,
          warehouse: TableRow.Warehouse,
          itemType: "package",
          currency: TableRow.Currency,
          maxReturnedOrderQuantity: TableRow.ApprovedQuantity,
        }),
      );
    } else if (TableRow.OfferId) {
      // Offer
      dispatch(
        addToReturnedOrderCart({
          productId: 0,
          offerId: TableRow.OfferId,
          packageId: 0,
          quantity: 1,
          price: TableRow.Price,
          image: TableRow.Image,
          name: TableRow.Offer?.Name,
          nameAr: TableRow.Offer?.NameAr,
          nameEn: TableRow.Offer?.NameEn,
          warehouse: TableRow.Warehouse,
          itemType: "offer",
          currency: TableRow.Currency,
          maxReturnedOrderQuantity: TableRow.ApprovedQuantity,
        }),
      );
    }
  };

  const handleRemoveItem = () => {
    const currentQty = itemQuantities[uniqueKey] || 0;
    if (currentQty <= 0) return;

    dispatch(
      decreaseReturnedOrderQuantity({
        productId: TableRow.ProductId || 0,
        offerId: TableRow.OfferId || 0,
        packageId: TableRow.PackageId || 0,
        quantity: 1,
        warehouse: TableRow.Warehouse,
      }),
    );
  };

  // Continuous increment effect
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (isIncreasing[uniqueKey]) {
      const increment = () => {
        setItemQuantities((prev) => {
          const currentQty = prev[uniqueKey] || 0;
          // stop at max
          if (currentQty >= TableRow?.ApprovedQuantity) return prev;
          return { ...prev, [uniqueKey]: currentQty + 1 };
        });
        handleAddItemToCart();
        timeoutId = setTimeout(increment, CardCounterSpeed);
      };
      increment();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isIncreasing[uniqueKey]]);

  // Continuous decrement effect
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (isDecreasing[uniqueKey]) {
      const decrement = () => {
        setItemQuantities((prev) => {
          const currentQty = prev[uniqueKey] || 0;
          if (currentQty <= 0) return prev; // no negative
          return { ...prev, [uniqueKey]: currentQty - 1 };
        });
        handleRemoveItem();
        timeoutId = setTimeout(decrement, CardCounterSpeed);
      };
      decrement();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isDecreasing[uniqueKey]]);

  // Initialize quantity from TableRow prop changes
  useEffect(() => {
    setItemQuantities((prev) => ({ ...prev, [uniqueKey]: TableRow.quantity }));
  }, [TableRow.quantity, uniqueKey]);

  return (
    <div className="flex items-center gap-0">
      <button
        onMouseDown={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: true }))
        }
        onMouseUp={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onMouseLeave={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        className={`flex h-8 w-8 cursor-pointer items-center justify-center bg-componentFilterOutline ${
          i18n.language.slice(0, 2) === ArabicCode
            ? `rounded-r-md`
            : `rounded-l-md`
        }`}
        disabled={(itemQuantities[uniqueKey] || 0) <= 0}
      >
        <img
          src={
            (itemQuantities[uniqueKey] || 0) === 1
              ? `${BASE_LOCAL_IMAGES_DIR}/${cart_trash_IMG}`
              : `${BASE_LOCAL_IMAGES_DIR}/${minus_button_IMG}`
          }
          alt={(itemQuantities[uniqueKey] || 0) === 1 ? "trash" : "minus"}
        />
      </button>
      <input
        type="text"
        name="quantity"
        value={itemQuantities[uniqueKey] || 0}
        onKeyPress={(event) => {
          if (!/[0-9]/.test(event.key)) {
            event.preventDefault();
          }
        }}
        readOnly
        className="h-8 bg-primaryLighter text-center outline-none"
        placeholder="0"
      />
      <button
        onMouseDown={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: true }))
        }
        onMouseUp={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onMouseLeave={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        className={`flex items-center justify-center bg-primaryMain ${
          i18n.language === ArabicCode ? "rounded-l-md" : "rounded-r-md"
        } h-8 w-8 cursor-pointer`}
        disabled={(itemQuantities[uniqueKey] || 0) >= TableRow?.Quantity}
      >
        <img src={`${BASE_LOCAL_IMAGES_DIR}/${white_plus_IMG}`} alt="plus" />
      </button>
    </div>
  );
};

export default ReturnedOrderTableQuantityItem;
